import { Routes } from '@lib/routes'
import { colors } from '@styles/colors'
import { Block } from 'baseui/block'
import Link from 'next/link'
import { padding } from 'polished'
import { useStyletron } from 'styletron-react'
import { BaseCell } from '../base-cell'
import { BaseGrid } from '../base-grid'
import { LabelS, LabelXS, ParagraphXS } from '../base-typography'
import AppleStore from 'public/svg/stores/apple-store.svg'
import GooglePlay from 'public/svg/stores/google-play.svg'
import { borderRadius } from '@styles/utils'
import { Logo } from '../logo'
import { Icon } from '../icon'
import { FlexGrid, FlexGridItem } from 'baseui/flex-grid'
import { mediaQuery } from '@styles/media-query'
import { footerMenus, socialMedia } from './menus'
import { BaseBlock } from '../base-block'
import { useState } from 'react'
import { zIndexes } from '@styles/z-indexes'
import { useMe } from '@modules/users/hooks/use-me'

export const Footer = () => {
  const [css] = useStyletron()
  const [isOpen, setIsOpen] = useState(false)
  const { me } = useMe()

  return (
    <footer
      className={css({
        width: '100%',
        backgroundColor: colors.gray50,
        ...padding('64px', 0),
        boxSizing: 'border-box',
        zIndex: zIndexes.footer,
        marginTop: '200px',
      })}
    >
      <BaseGrid>
        <BaseCell span={[4, 8, 12]}>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              rowGap: '48px',
              [mediaQuery.DESKTOP]: {
                flexDirection: 'row',
                columnGap: '16px',
              },
            })}
          >
            <Block width="100%" maxWidth="248px">
              <Logo size="m" />
            </Block>
            <Block flex="1 1 auto">
              <FlexGrid
                flexGridColumnCount={[2, 2, 4, 3]}
                flexGridColumnGap="16px"
                flexGridRowGap="48px"
              >
                {footerMenus.map(({ title, menus }) => (
                  <FlexGridItem
                    key={title}
                    className={css({
                      display: 'flex',
                      flexDirection: 'column',
                      rowGap: '12px',
                      alignItems: 'flex-start',
                    })}
                  >
                    <LabelS weight={700} marginBottom="8px">
                      {title}
                    </LabelS>
                    {menus.map(({ label, href, role, blank }) =>
                      me?.role ? (
                        !role?.includes(me.role) && (
                          <Link href={href} passHref key={label}>
                            <a target={blank ? '_blank' : undefined}>
                              <LabelS
                                color={colors.gray500}
                                $style={{
                                  ':hover': { textDecoration: 'underline' },
                                }}
                              >
                                {label}
                              </LabelS>
                            </a>
                          </Link>
                        )
                      ) : (
                        <Link href={href} passHref key={label}>
                          <a target={blank ? '_blank' : undefined}>
                            <LabelS
                              color={colors.gray500}
                              $style={{
                                ':hover': { textDecoration: 'underline' },
                              }}
                            >
                              {label}
                            </LabelS>
                          </a>
                        </Link>
                      )
                    )}
                  </FlexGridItem>
                ))}
              </FlexGrid>
            </Block>
            <Block
              className={css({
                display: 'flex',
                columnGap: '12px',
                maxWidth: '248px',
                width: '100%',
                [mediaQuery.DESKTOP]: {
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                },
              })}
            >
              {socialMedia.map(({ label, href, icon }) => (
                <Link href={href} passHref key={label}>
                  <a
                    aria-label={label}
                    target="_blank"
                    className={css({
                      display: 'flex',
                      ...padding('2px'),
                      ...borderRadius(32),
                    })}
                  >
                    <Icon type={icon} color={colors.gray400} />
                  </a>
                </Link>
              ))}
            </Block>
          </div>
          <BaseBlock marginTop={['48px', '48px', '48px', '120px']}>
            <button
              className={css({
                border: 'none',
                backgroundColor: 'transparent',
                ...padding('0px'),
                display: 'flex',
                alignItems: 'center',
                columnGap: '4px',
                cursor: 'pointer',
                marginBottom: '8px',
              })}
              onClick={() => {
                setIsOpen((prev) => !prev)
              }}
            >
              <LabelXS color={colors.gray500}>(주) 테서 사업자 정보</LabelXS>
              <Icon
                type={isOpen ? 'ChevronUp' : 'ChevronDown'}
                size={12}
                color={colors.gray500}
              />
            </button>
            {isOpen && (
              <ParagraphXS
                color={colors.gray500}
                marginBottom="8px"
                $style={{
                  [mediaQuery.TABLET]: {
                    whiteSpace: 'pre',
                  },
                }}
              >
                {`(주) 테서 | 대표이사: 이수현 | 사업자등록번호: 404-81-46323 | 주소: 서울시 강남구 언주로 544, 삼본빌딩 4F \n연락처: +82-2-575-1593 | tesser@tesser.co.kr`}
              </ParagraphXS>
            )}
            <BaseBlock
              className={css({
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
                rowGap: '48px',
                [mediaQuery.TABLET]: {
                  flexDirection: 'row',
                },
              })}
            >
              <Block>
                <BaseBlock display="flex" alignItems="center" columnGap="10px">
                  <PolicyLink
                    label="서비스 이용약관"
                    href={Routes.LEGAL_TERMS_OF_SERVICE}
                  />
                  <Block
                    width="1px"
                    height="10px"
                    backgroundColor={colors.gray400}
                  />
                  <PolicyLink
                    label="개인정보 취급방침"
                    href={Routes.LEGAL_PRIVACY_POLICY}
                  />
                </BaseBlock>
                <LabelXS marginTop="8px" color={colors.gray500}>
                  ⓒ 2022 Tesser Corp. All rights reserved.
                </LabelXS>
              </Block>
              <Block
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '10px',
                })}
              >
                <Link href={Routes.PLAY_STORE} passHref>
                  <a target="_blank" aria-label="google play">
                    <GooglePlay className={css({ ...borderRadius(6) })} />
                  </a>
                </Link>
                <Link href={Routes.APP_STORE} passHref>
                  <a target="_blank" aria-label="apple store">
                    <AppleStore className={css({ ...borderRadius(6) })} />
                  </a>
                </Link>
              </Block>
            </BaseBlock>
          </BaseBlock>
        </BaseCell>
      </BaseGrid>
    </footer>
  )
}

interface PolicyLinkProps {
  label: string
  href: string
}

const PolicyLink = ({ href, label }: PolicyLinkProps) => {
  const [css] = useStyletron()

  return (
    <Link href={href} passHref>
      <a
        target="_blank"
        className={css({
          fontSize: '12px',
          lineHeight: '16px',
          color: colors.gray500,
        })}
      >
        {label}
      </a>
    </Link>
  )
}
